/**
 * Centralised query-key factories for TanStack Query.
 *
 * Convention: each domain exports a factory whose root key matches the domain
 * name. Hierarchical keys allow targeted invalidation (e.g. invalidate every
 * `team.*` key after a team update without touching `company.*`).
 */

export const teamKeys = {
  all: ["team"] as const,
  detail: (teamId: string) => [...teamKeys.all, teamId] as const,
  prices: () => [...teamKeys.all, "prices"] as const,
  billing: (teamId: string) => [...teamKeys.detail(teamId), "billing"] as const,
  llm: (teamId: string) => [...teamKeys.detail(teamId), "llm"] as const,
  users: (teamId: string) => [...teamKeys.detail(teamId), "users"] as const,
  invitations: (teamId: string) =>
    [...teamKeys.detail(teamId), "invitations"] as const,
};

export const companyKeys = {
  all: ["company"] as const,
  detail: (companyId: string) => [...companyKeys.all, companyId] as const,
  news: (companyId: string) =>
    [...companyKeys.detail(companyId), "news"] as const,
  employees: (companyId: string) =>
    [...companyKeys.detail(companyId), "employees"] as const,
};

export const userKeys = {
  all: ["user"] as const,
  me: () => [...userKeys.all, "me"] as const,
};

export const creditsKeys = {
  all: ["credits"] as const,
  balance: (teamId: string) =>
    [...creditsKeys.all, teamId, "balance"] as const,
  stacks: (teamId: string) => [...creditsKeys.all, teamId, "stacks"] as const,
};

export const listingKeys = {
  all: ["listing"] as const,
  list: (teamId: string) => [...listingKeys.all, teamId] as const,
  detail: (listingId: string) =>
    [...listingKeys.all, "detail", listingId] as const,
};

export const searchKeys = {
  all: ["search"] as const,
  /** Every saved search of a team the cache holds. */
  team: (teamId: string) => [...searchKeys.all, teamId] as const,
  /** A saved search as `GET /search-engine/search/:id` returns it. */
  detail: (teamId: string, searchId: string) =>
    [...searchKeys.team(teamId), searchId] as const,
};

/**
 * The recently viewed companies of the chat's home screen, one entry per page
 * size asked for: `all` invalidates every page at once.
 */
export const chatRecentCompaniesKeys = {
  all: ["chat-recent-companies"] as const,
  page: (limit: number) => [...chatRecentCompaniesKeys.all, limit] as const,
};
